import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import {
  selectCurrentContentId,
  selectGridPanel,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import { selectShowAllScreens } from 'features/screenshare/screenshareSlice';
import { selectContentLibraryOpenedFile } from 'features/content-library/contentLibrarySlice';
import { isPrivateWhiteboard } from 'features/content-library/utils/isPrivateWhiteboard';
import { contentLocked, contentUnlocked } from 'features/layout/features/content/actions';
import { openModal } from 'features/modal/modalSlice';

export const useLockContentControl = () => {
  const { hasPermissions } = usePermissions();
  const { t } = useTranslation('room');
  const dispatch = useAppDispatch();

  const canLockContent = hasPermissions(PermissionTypes.lockContent);

  const showAllScreens = useAppSelector(selectShowAllScreens);
  const currentGridPanel = useAppSelector(selectGridPanel);
  const lockedContent = useAppSelector(selectLockedContent);
  const currentContentId = useAppSelector(selectCurrentContentId);

  const openedFile = useAppSelector(selectContentLibraryOpenedFile);

  const privateWhiteboard = isPrivateWhiteboard(openedFile);

  const controlEnabled =
    canLockContent && !privateWhiteboard && (!showAllScreens || currentGridPanel !== 'screenshare');

  const isCurrentContentLocked = lockedContent?.id === currentContentId;

  const handleClick = () => {
    if (isCurrentContentLocked) {
      dispatch(contentUnlocked());
    } else {
      if (lockedContent) {
        if (currentContentId && currentContentId !== lockedContent.id) {
          dispatch(openModal('lockContentConfirm'));
          return;
        }
      }

      dispatch(contentLocked());
    }
  };

  const label = isCurrentContentLocked ? t('grid.unlock_content') : t('grid.lock_content');

  return {
    controlEnabled,
    canLockContent,
    isCurrentContentLocked: lockedContent?.id === currentContentId,
    isLocked: !!lockedContent,
    handleClick,
    label,
    tooltip: label,
    icon: isCurrentContentLocked ? 'lock' : 'unlock',
  };
};
